import { useCallback, useEffect, useState } from "react";
import { Gift, Loader2 } from "lucide-react";
import api, { errMsg, money } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const field = "h-11 border-ink-600 bg-ink-800 text-slate-100 focus-visible:ring-gold";

export const LoyaltyPanel = ({ company, setCompany }) => {
  const [form, setForm] = useState({ enabled: false, rides_required: 5, credit_amount: 25 });
  const [members, setMembers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");

  useEffect(() => {
    if (!company) return;
    setForm({
      enabled: !!company.loyalty_enabled,
      rides_required: company.loyalty_rides_required ?? 5,
      credit_amount: company.loyalty_credit_amount ?? 25,
    });
  }, [company]);

  const load = useCallback(() => {
    api.get("/loyalty/members").then(({ data }) => setMembers(data.members)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy(true); setError(""); setMsg("");
    try {
      const { data } = await api.put("/company/loyalty", {
        enabled: form.enabled,
        rides_required: Number(form.rides_required) || 5,
        credit_amount: Number(form.credit_amount) || 25,
      });
      setCompany(data);
      setMsg("Saved.");
      load();
    } catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="loyalty-panel" className="space-y-5">
      <p className="text-sm text-slate-400">
        Reward the passengers who keep coming back: after a set number of completed rides they earn a
        credit that comes off their next booking automatically.
      </p>
      {error && <p data-testid="loyalty-error" className="text-sm text-red-400">{error}</p>}
      {msg && <p data-testid="loyalty-msg" className="text-sm text-emerald-400">{msg}</p>}

      <button type="button" data-testid="loyalty-toggle" onClick={() => setForm({ ...form, enabled: !form.enabled })}
        className={`flex w-full items-center gap-3 rounded-xl border p-4 text-left transition-colors ${
          form.enabled ? "border-gold/50 bg-gold/10" : "border-ink-600 bg-ink-800 hover:border-gold/30"}`}>
        <Gift className={`h-4 w-4 ${form.enabled ? "text-gold" : "text-slate-500"}`} />
        <span className="text-sm text-slate-200">
          Loyalty rewards are {form.enabled ? "on" : "off"}
        </span>
      </button>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label className="mb-2 block font-mono text-xs uppercase tracking-widest text-gold">Rides required</label>
          <Input data-testid="loyalty-rides" type="number" min="2" max="50" value={form.rides_required}
            onChange={(e) => setForm({ ...form, rides_required: e.target.value })} className={field} />
        </div>
        <div>
          <label className="mb-2 block font-mono text-xs uppercase tracking-widest text-gold">Credit earned</label>
          <Input data-testid="loyalty-credit" type="number" step="0.01" min="1" value={form.credit_amount}
            onChange={(e) => setForm({ ...form, credit_amount: e.target.value })} className={field} />
        </div>
      </div>
      <Button data-testid="loyalty-save" disabled={busy} onClick={save}
        className="h-11 bg-gold px-8 text-ink-900 hover:bg-gold-light">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save changes"}
      </Button>

      {members.length > 0 && (
        <div className="border-t border-ink-600 pt-5" data-testid="loyalty-members">
          <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Your repeat passengers</p>
          <div className="mt-3 space-y-2">
            {members.slice(0, 12).map((m) => (
              <div key={m.email} className="flex flex-wrap items-center justify-between gap-3 text-sm">
                <span className="text-slate-300">{m.name || m.email}</span>
                <span className="font-mono text-[11px] text-slate-500">
                  {m.rides} ride{m.rides === 1 ? "" : "s"} · {m.rides_to_next} to next
                  {m.credit_waiting > 0 && (
                    <span className="ml-2 text-emerald-300">{money(m.credit_waiting)} waiting</span>
                  )}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
